import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator as CalcIcon, ArrowSquareOut, FilePdf } from "@phosphor-icons/react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { advisorHeaders } from "../lib/clarity";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { SettingsEditor } from "../components/rollover/SettingsEditor";
import { money } from "../components/rollover/fields";

const LABELS = { "401k": "401(k)", trad_ira: "Trad IRA", roth_ira: "Roth IRA", "403b": "403(b)", "457": "457(b)" };
const EDITABLE = ["booking_url", "tax", "client", "qualified", "iul_presets", "fia_presets"];
const pick = (s) => Object.fromEntries(EDITABLE.map((k) => [k, s[k]]));

const ScenarioList = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/rollover/scenarios", advisorHeaders()).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  const pdf = async (id) => {
    const res = await api.get(`/rollover/scenarios/${id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = `rollover-${id}.pdf`; document.body.appendChild(a); a.click(); a.remove();
  };
  if (!items) return <p className="text-sm text-exodus-muted">Loading…</p>;
  if (!items.length) return <p data-testid="ro-admin-empty" className="text-sm text-exodus-muted">No rollover scenarios yet. They show up here automatically when signed-in clients reach their results.</p>;
  return (
    <div className="border border-exodus-border overflow-x-auto">
      <table data-testid="ro-admin-scenarios" className="w-full text-sm min-w-[900px]">
        <thead className="bg-exodus-elevated"><tr className="text-left text-[10px] uppercase tracking-widest text-exodus-muted">
          {["Client", "From → To", "Amount", "Income / yr (chosen)", "Keep / IUL / FIA income", "Flags", "Updated", ""].map((h) => <th key={h} className="p-3 font-normal">{h}</th>)}
        </tr></thead>
        <tbody>
          {items.map((x) => {
            const c = Object.fromEntries((x.compare || []).map((k) => [k.key, k]));
            return (
              <tr key={x.id} data-testid={`ro-admin-row-${x.id}`} className="border-t border-exodus-border align-top">
                <td className="p-3"><p>{x.full_name || "—"}</p><p className="text-xs text-exodus-muted">{x.email}</p></td>
                <td className="p-3">{LABELS[x.source]} → {x.product?.toUpperCase()}</td>
                <td className="p-3 font-mono">{money(x.amount)}</td>
                <td className="p-3 font-mono gold-text">{money(c[x.product]?.annual_net_income)}</td>
                <td className="p-3 font-mono text-xs">{money(c.keep?.annual_net_income)} / {money(c.iul?.annual_net_income)} / {money(c.fia?.annual_net_income)}</td>
                <td className="p-3 text-xs text-exodus-muted">{(x.warnings || []).filter((w) => !["rmd"].includes(w)).join(", ") || "—"}{x.emailed_at ? " · emailed" : ""}</td>
                <td className="p-3 text-xs text-exodus-muted whitespace-nowrap">{new Date(x.updated_at).toLocaleString()}</td>
                <td className="p-3 whitespace-nowrap">
                  <a href={`/rollover/s/${x.id}`} target="_blank" rel="noreferrer" data-testid={`ro-admin-open-${x.id}`} className="inline-flex text-exodus-gold mr-3"><ArrowSquareOut size={16} /></a>
                  <button type="button" data-testid={`ro-admin-pdf-${x.id}`} onClick={() => pdf(x.id)} className="text-exodus-gold"><FilePdf size={16} /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AssumptionsEditor = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rev, setRev] = useState(0);
  useEffect(() => { api.get("/rollover/settings").then((r) => setS(pick(r.data))); }, []);
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/rollover/settings", s, advisorHeaders()); setS(pick(r.data)); toast.success("Default assumptions saved. New calculator sessions use them right away."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset all rollover assumptions to the built-in defaults?")) return;
    const r = await api.post("/admin/rollover/settings/reset", {}, advisorHeaders());
    setS(pick(r.data)); setRev((x) => x + 1); toast.success("Reset to built-in defaults.");
  };
  if (!s) return <p className="text-sm text-exodus-muted">Loading…</p>;
  return (
    <div className="space-y-6">
      <p className="text-xs text-exodus-muted max-w-2xl">These are the defaults and presets clients see when they open the Rollover calculator. Presets map to the Conservative, Baseline and Aggressive scenarios. Clients can still edit every value for their own session.</p>
      <div className="border border-exodus-border p-6 bg-exodus-surface"><SettingsEditor key={rev} value={s} onChange={setS} /></div>
      <div className="flex gap-3">
        <button type="button" data-testid="ro-admin-save" onClick={save} disabled={saving} className="btn-gold disabled:opacity-50">{saving ? "Saving…" : "Save assumptions"}</button>
        <button type="button" data-testid="ro-admin-reset" onClick={reset} className="text-sm text-exodus-muted hover:text-exodus-gold px-4">Reset to defaults</button>
      </div>
    </div>
  );
};

export default function AdminRollover() {
  return (
    <DashboardLayout>
      <div data-testid="admin-rollover-page" className="p-6 lg:p-10">
        <div className="flex items-center gap-3 mb-2"><CalcIcon size={22} className="text-exodus-gold" /><h1 className="font-display text-3xl">Rollover Calculator</h1></div>
        <p className="text-sm text-exodus-muted mb-8">Client rollover scenarios and the default assumptions behind the calculator.</p>
        <Tabs defaultValue="scenarios">
          <TabsList className="bg-exodus-surface border border-exodus-border">
            <TabsTrigger data-testid="ro-admin-tab-scenarios" value="scenarios">Client scenarios</TabsTrigger>
            <TabsTrigger data-testid="ro-admin-tab-assumptions" value="assumptions">Default assumptions</TabsTrigger>
          </TabsList>
          <TabsContent value="scenarios" className="mt-6"><ScenarioList /></TabsContent>
          <TabsContent value="assumptions" className="mt-6"><AssumptionsEditor /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
